import experiments from '@reverbdotcom/commons/src/experiments';
import gql from 'graphql-tag';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import { nestedFilterFragment } from '@reverbdotcom/commons/src/components/rql_facets';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { mParticleListingFields } from '@reverbdotcom/commons/src/components/with_mparticle_listings';

import {
  ListingsCollection as ListingsCollectionClass,
} from '@reverbdotcom/commons/src/components/listings_collection';

import {
  IUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';

export {
  mapUrlParams as mapParams,
  updateCategoryAsParams,
} from '../marketplace/map_params';

export type {
  MeRecommendedListingsGrid,
  MeRecommendedListings,
} from '../../schemas/rql';

export {
  withUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';

export const COMPONENT_NAME = 'RecommendedListings';

export function useRecommendedListingsTracking(props: IUserContext) {
  const mParticleViewData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
    experiments: formatExperimentsForEventAttributes(
      props.user,
      [experiments.WEB_RECOMMENDATIONS],
    ),
  };

  useViewTracking(mParticleViewData, true);
}

export function experimentEnabled(props: IUserContext) {
  return isExperimentEnabled(
    props.user,
    experiments.WEB_RECOMMENDATIONS,
  );
}

export const gridQuery = gql`
  query MeRecommendedListingsGrid(
    $query: String
    $currency: String
    $priceMax: String
    $priceMin: String
    $decades: [String]
    $yearMax: String
    $yearMin: String
    $sortSlug: String
    $limit: Int
    $offset: Int
    $categorySlugs: [String]
    $brandSlugs: [String]
    $aggs: [reverb_search_ListingsSearchRequest_Aggregation]
    $conditionSlugs: [String]
    $onSale: Boolean
    $handmade: Boolean
    $freeShipping: Boolean
    $freeExpeditedShipping: Boolean
    $acceptsOffers: Boolean
    $acceptsGiftCards: Boolean
    $preferredSeller: Boolean
    $acceptsPaymentPlans: Boolean
    $itemRegion: String
    $shippingRegionCode: [String]
    $showOnlySold: Boolean
    $showSold: Boolean
    $traitValues: [String]
    $itemState: [String]
    $itemCity: [String]
    $shopSlug: String
    $curatedSetSlugs: [String]
    $saleSlugs: [String]
    $cspSlug: String
    $sort: reverb_search_ListingsSearchRequest_Sort
    $combinedShipping: Boolean
    $acceptsAffirm: Boolean
    $terminateEarly: Boolean
    $collapsible: reverb_search_ListingsSearchRequest_Collapsible
    $fallbackToOr: Boolean
    $withProximityFilter: Input_reverb_search_ProximityFilterRequest
    $localPickup: Boolean
    $shouldSkipTracking: Boolean!
  ) {
    me {
      uuid
      recommendedListings(
        input: {
          query: $query
          currency: $currency
          priceMax: $priceMax
          priceMin: $priceMin
          decades: $decades
          yearMax: $yearMax
          yearMin: $yearMin
          sortSlug: $sortSlug
          traitValues: $traitValues
          limit: $limit
          offset: $offset
          categorySlugs: $categorySlugs
          brandSlugs: $brandSlugs
          withAggregations: $aggs
          conditionSlugs: $conditionSlugs
          onSale: $onSale
          handmade: $handmade
          freeShipping: $freeShipping
          freeExpeditedShipping: $freeExpeditedShipping
          acceptsOffers: $acceptsOffers
          acceptsGiftCards: $acceptsGiftCards
          preferredSeller: $preferredSeller
          acceptsPaymentPlans: $acceptsPaymentPlans
          itemRegion: $itemRegion
          shippingRegionCodes: $shippingRegionCode
          showOnlySold: $showOnlySold
          showSold: $showSold
          itemState: $itemState
          itemCity: $itemCity
          shopSlug: $shopSlug
          curatedSetSlugs: $curatedSetSlugs
          saleSlugs: $saleSlugs
          cspSlug: $cspSlug
          sort: $sort
          combinedShipping: $combinedShipping
          acceptsAffirm: $acceptsAffirm
          terminateEarly: $terminateEarly
          collapsible: $collapsible
          fallbackToOr: $fallbackToOr
          withProximityFilter: $withProximityFilter
          localPickup: $localPickup
        }
      ) {
        total
        offset
        limit
        filters {
          ... NestedFilter
        }
        listings {
          _id
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
        }
      }
    }
  }
  ${ListingsCollectionClass.fragments.listing}
  ${mParticleListingFields}
  ${nestedFilterFragment}
`;

export const rowQuery = gql`
  query MeRecommendedListings(
    $limit: Int
    $offset: Int
    $experimentDisabled: Boolean!
    $shouldSkipTracking: Boolean!
  ) {
    me @skip(if: $experimentDisabled) {
      uuid
      recommendedListings(
        input: {
          limit: $limit
          offset: $offset
        }
      ) {
        listings {
          _id
          ... ListingsCollection
          ... mParticleListingFields @skip(if: $shouldSkipTracking)
        }
      }
    }
  }
  ${ListingsCollectionClass.fragments.listing}
  ${mParticleListingFields}
`;
